(() => {
  if (customElements.get('olea-contact')) return;

  const fields = new Map([
    ['name', 200],
    ['email', 254],
    ['order_number', 100],
    ['product', 300],
    ['body', 5000],
    ['subject', 20]
  ]);
  const subjectTypes = new Set(['product', 'order', 'delivery', 'return', 'other']);
  const retention = 30 * 60 * 1000;

  class OleaContact extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('form.olea-contact__native-form');
      if (!this.form) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const customer = /^\d+$/.test(this.dataset.contactCustomerId || '') ? this.dataset.contactCustomerId : 'guest';
      this.storageKey = `olea:contact:${location.pathname}:${this.dataset.contactSection || 'main'}:${customer}`;
      this.serverStatus = this.form.querySelector('[data-contact-submit-status]');
      this.submitting = false;
      if (this.serverStatus?.dataset.contactSubmitStatus === 'success') this.clearSaved();
      else this.restoreSaved();
      this.form.addEventListener('submit', event => this.onSubmit(event), { signal });
      window.addEventListener('pageshow', () => this.resetSubmit(), { signal });
      if (this.serverStatus) this.statusFrame = requestAnimationFrame(() => this.serverStatus?.focus({ preventScroll: true }));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.statusFrame);
      this.resetSubmit();
    }

    input(name) {
      return this.form.querySelector(`[name="contact[${name}]"]`);
    }

    radios() {
      return Array.from(this.form.querySelectorAll('input[type="radio"][name="contact[subject]"]'));
    }

    clearSaved() {
      try { sessionStorage.removeItem(this.storageKey); } catch {}
    }

    saveFields() {
      if (this.dataset.preserveFields !== 'true') return;
      const values = {};
      fields.forEach((max, name) => {
        const input = name === 'subject' ? this.radios().find(radio => radio.checked) : this.input(name);
        if (input && (name !== 'subject' || subjectTypes.has(input.value))) values[name] = String(input.value).slice(0, max);
      });
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ time: Date.now(), values })); } catch {}
    }

    restoreSaved() {
      if (this.dataset.preserveFields !== 'true') {
        this.clearSaved();
        return;
      }
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch { this.clearSaved(); return; }
      if (!saved) return;
      const age = Date.now() - saved.time;
      if (!Number.isFinite(age) || age < 0 || age > retention || !saved.values || typeof saved.values !== 'object' || Array.isArray(saved.values)) {
        this.clearSaved();
        return;
      }
      fields.forEach((max, name) => {
        const value = saved.values[name];
        if (typeof value !== 'string' || value.length > max) return;
        if (name === 'subject') {
          if (subjectTypes.has(value)) this.radios().forEach(radio => { radio.checked = radio.value === value; });
          return;
        }
        const input = this.input(name);
        if (!input) return;
        const serverEcho = this.serverStatus?.dataset.contactSubmitStatus === 'error' && ['name', 'email', 'body'].includes(name) && input.value.trim();
        if (!serverEcho) input.value = value;
      });
    }

    onSubmit(event) {
      if (event.defaultPrevented) return;
      if (this.submitting || !this.form.checkValidity()) {
        event.preventDefault();
        return;
      }
      this.saveFields();
      this.submitting = true;
      this.form.setAttribute('aria-busy', 'true');
      this.submitButton = event.submitter || this.form.querySelector('button[type="submit"]');
      if (this.submitButton) this.submitButton.disabled = true;
      queueMicrotask(() => { if (event.defaultPrevented) this.resetSubmit(); });
    }

    resetSubmit() {
      this.submitting = false;
      this.form?.removeAttribute('aria-busy');
      if (this.submitButton) this.submitButton.disabled = false;
    }
  }

  customElements.define('olea-contact', OleaContact);
})();
